import { formatCurrency, formatString, MONTHS } from "./functions.js";
import { DashboardResultLine } from "./server-types/DashboardResultLine.js";
import { TeamMember } from "./server-types/TeamMember.js";

import { app } from "./session.js";

class SalesReportManager {
    private currentYear: number;
    private teamMembers: TeamMember[] = [];
    private plans: DashboardResultLine[] = [];

    constructor() {
        this.currentYear = this.getDefaultYear();
        this.init();
    }

    private getDefaultYear(): number {
        const currentDate = new Date();
        const futureDate = new Date();
        futureDate.setDate(currentDate.getDate() + 30);
        return futureDate.getFullYear();
    }

    private async init() {
        try {
            await this.loadTeamMembers();
            await this.render();
            await this.loadPlans();
        } catch (error) {
            console.error('Failed to initialize Sales Report Manager:', error);
            this.showError('Failed to load data. Please refresh the page.');
        }
    }

    private async loadTeamMembers() {
        try {
            this.teamMembers = await app.API.getMemberList();
        } catch (error) {
            console.error('Failed to load team members:', error);
            throw error;
        }
    }

    private async loadPlans() {
        try {
            document.getElementById('plans-list').innerHTML = "Loading...";

            // Load plans for the selected year (using month 0 as default)
            this.plans = await app.API.dashboard(this.currentYear, 0);
            this.renderPlansList();
        } catch (error) {
            console.error('Failed to load plans:', error);
            this.showError('Failed to load plans for the selected year.');
        }
    }

    private async render() {
        const content = document.getElementById('content');
        if (!content) {
            throw new Error('Content element not found');
        }

        content.innerHTML = `
            <div class="sales-report-manager">
                <h2>Sales Report Plans Manager</h2>
                
                <div class="controls-section">
                    <div class="year-selector">
                        <label for="year-select">Select Year:</label>
                        <select id="year-select" class="form-control">
                            ${this.generateYearOptions()}
                        </select>
                    </div>
                    
                    <button id="create-plan-btn" class="btn btn-primary">Create New Plan</button>
                </div>

                <div id="plans-list" class="plans-section">
                    <div class="loading">Loading plans...</div>
                </div>

                <div id="create-plan-modal" class="modal" style="display: none;">
                    <div class="modal-content">
                        <h3>Create New Sales Plan</h3>
                        <form id="create-plan-form">
                            <div class="form-group">
                                <label for="team-member-select">Team Member:</label>
                                <select id="team-member-select" class="form-control" required>
                                    <option value="">Select Team Member</option>
                                    ${this.teamMembers.map(member => `<option value="${member.id}">${member.name}</option>`).join('')}
                                </select>
                            </div>

                            <div class="form-group">
                                <label for="plan-name">Plan Name (opt):</label>
                                <input type="text" id="plan-name" class="form-control">
                            </div>

                            <div class="form-group">
                                <label>Monthly Goals:</label>
                                <div class="monthly-goals">
                                    ${this.generateMonthlyInputs('create')}
                                </div>
                            </div>
                            
                            <div class="form-actions">
                                <button type="submit" class="btn btn-success">Create Plan</button>
                                <button type="button" id="cancel-create" class="btn btn-secondary">Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>

                <div id="edit-plan-modal" class="modal" style="display: none;">
                    <div class="modal-content">
                        <h3>Edit Sales Plan</h3>
                        <form id="edit-plan-form">
                            <input type="hidden" id="edit-plan-id">
                            <div class="form-group">
                                <label for="edit-plan-name">Plan Name (opt):</label>
                                <input type="text" id="edit-plan-name" class="form-control">
                            </div>
                            
                            <div class="form-group">
                                <label>Monthly Goals:</label>
                                <div class="monthly-goals" id="edit-monthly-goals">
                                    ${this.generateMonthlyInputs('edit')}
                                </div>
                            </div>
                            
                            <div class="form-actions">
                                <button type="submit" class="btn btn-success">Update Plan</button>
                                <button type="button" id="cancel-edit" class="btn btn-secondary">Cancel</button>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        `;

        this.attachEventListeners();
    }

    private generateYearOptions(): string {
        const currentYear = new Date().getFullYear();
        const startYear = 2025;
        const endYear = Math.max(currentYear + 1, this.currentYear);

        let options = '';
        for (let year = startYear; year <= endYear; year++) {
            const selected = year === this.currentYear ? 'selected' : '';
            options += `<option value="${year}" ${selected}>${year}</option>`;
        }
        return options;
    }

    private generateMonthlyInputs(prefix: string = ''): string {
        return MONTHS.map((month, index) => `
            <div class="month-input">
                <label for="${prefix}-month-${index}">${month}:</label>
                <input type="number" id="${prefix}-month-${index}" class="form-control" min="0" value="0" required>
            </div>
        `).join('');
    }

    private attachEventListeners() {
        // Year selector change
        const yearSelect = document.getElementById('year-select') as HTMLSelectElement;
        yearSelect?.addEventListener('change', (e) => {
            this.currentYear = parseInt((e.target as HTMLSelectElement).value);
            this.loadPlans();
        });

        // Create plan button
        const createPlanBtn = document.getElementById('create-plan-btn');
        createPlanBtn?.addEventListener('click', () => {
            this.showCreateModal();
        });

        // Create plan form
        const createForm = document.getElementById('create-plan-form');
        createForm?.addEventListener('submit', (e) => {
            e.preventDefault();
            this.handleCreatePlan();
        });

        // Edit plan form
        const editForm = document.getElementById('edit-plan-form');
        editForm?.addEventListener('submit', (e) => {
            e.preventDefault();
            this.handleEditPlan();
        });

        // Modal cancel buttons
        document.getElementById('cancel-create')?.addEventListener('click', () => {
            this.hideCreateModal();
        });

        document.getElementById('cancel-edit')?.addEventListener('click', () => {
            this.hideEditModal();
        });

        // Close modals when clicking outside
        document.addEventListener('click', (e) => {
            const target = e.target as HTMLElement;
            if (target.classList.contains('modal')) {
                this.hideAllModals();
            }
        });
    }

    private renderPlansList() {
        const plansList = document.getElementById('plans-list');
        if (!plansList) return;

        if (this.plans.length === 0) {
            plansList.innerHTML = `
                <div class="no-plans">
                    <p>No plans found for ${this.currentYear}.</p>
                    <p>Create a new plan to get started.</p>
                </div>
            `;
            return;
        }

        // Group plans by user
        const plansByUser = this.groupPlansByUser();

        let html = '<table>';

        html += `<thead>
            <tr>
                <th>Name</th>
                <th>Plan Name</th>
                <th>Annual Goal</th>
                <th>Actions</th>
            </tr>
        </thead>
        <tbody>`;

        for (const [_, userPlans] of Object.entries(plansByUser)) {
            for (const plan of userPlans) {
                html += `<tr>
                    <td>${formatString(plan.userName)}</td>
                    <td>${formatString(plan.planName)}</td>
                    <td>${formatCurrency(plan.annualGoal)}</td>
                    <td>
                    <div class="plan-actions">
                        <button class="btn btn-sm btn-outline-primary edit-plan-btn" data-plan-id="${plan.id}">
                            Edit
                        </button>
                        <button class="btn btn-sm btn-outline-danger delete-plan-btn" data-plan-id="${plan.id}">
                            Delete
                        </button>
                    </div>
                    </td>
                </tr>`;
            }
        }

        html += `</tbody>
        </table>`;
        plansList.innerHTML = html;

        // Attach plan-specific event listeners
        this.attachPlanEventListeners();
    }

    private groupPlansByUser(): Record<string, DashboardResultLine[]> {
        return this.plans.reduce((acc, plan) => {
            if (!acc[plan.userId]) {
                acc[plan.userId] = [];
            }
            acc[plan.userId].push(plan);
            return acc;
        }, {} as Record<string, DashboardResultLine[]>);
    }

    private attachPlanEventListeners() {
        // Edit buttons
        document.querySelectorAll('.edit-plan-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const planId = (e.target as HTMLElement).getAttribute('data-plan-id');
                if (planId) {
                    this.editPlan(planId);
                }
            });
        });

        // Delete buttons
        document.querySelectorAll('.delete-plan-btn').forEach(btn => {
            btn.addEventListener('click', (e) => {
                const planId = (e.target as HTMLElement).getAttribute('data-plan-id');
                if (planId) {
                    this.deletePlan(planId);
                }
            });
        });
    }

    private showCreateModal() {
        const modal = document.getElementById('create-plan-modal');
        if (modal) {
            modal.style.display = '';
            // Reset form
            const form = document.getElementById('create-plan-form') as HTMLFormElement;
            form?.reset();
        }
    }

    private hideCreateModal() {
        const modal = document.getElementById('create-plan-modal');
        if (modal) {
            modal.style.display = 'none';
        }
    }

    private showEditModal() {
        const modal = document.getElementById('edit-plan-modal');
        if (modal) {
            modal.style.display = '';
        }
    }

    private hideEditModal() {
        const modal = document.getElementById('edit-plan-modal');
        if (modal) {
            modal.style.display = 'none';
        }
    }

    private hideAllModals() {
        this.hideCreateModal();
        this.hideEditModal();
    }

    private async handleCreatePlan() {
        try {
            const planName = (document.getElementById('plan-name') as HTMLInputElement).value.trim();
            const teamMemberId = (document.getElementById('team-member-select') as HTMLSelectElement).value;

            if (!teamMemberId) {
                this.showError('Please fill in all required fields.');
                return;
            }

            const months: number[] = [];
            for (let i = 0; i < MONTHS.length; i++) {
                const input = document.getElementById(`create-month-${i}`) as HTMLInputElement;
                months.push(parseInt(input.value) || 0);
            }

            const planData = {
                planName,
                userId: teamMemberId,
                year: this.currentYear,
                months
            };

            // Call the API to create the plan
            try {
                await app.API.createPlan(planData);
                this.showSuccess('Plan created successfully!');
                this.hideCreateModal();
                await this.loadPlans(); // Refresh the plans list
            } catch (error) {
                console.error('Error creating plan:', error);
                this.showError('An error occurred while creating the plan.');
            }
        } catch (error) {
            console.error('Error creating plan:', error);
            this.showError('An error occurred while creating the plan.');
        }
    }

    private async editPlan(planId: string) {
        try {
            // Get the plan data
            const plan = (await app.API.getPlan(planId)).value;

            if (plan) {
                // Populate the edit form
                (document.getElementById('edit-plan-id') as HTMLInputElement).value = planId;
                (document.getElementById('edit-plan-name') as HTMLInputElement).value = plan.planName;

                // Populate monthly goals
                plan.months.forEach((goal: number, index: number) => {
                    const input = document.getElementById(`edit-month-${index}`) as HTMLInputElement;
                    if (input) {
                        input.value = goal.toString();
                    }
                });

                this.showEditModal();
            }
        } catch (error) {
            console.error('Error loading plan for edit:', error);
            this.showError('Failed to load plan data for editing.');
        }
    }

    private async handleEditPlan() {
        try {
            const planId = (document.getElementById('edit-plan-id') as HTMLInputElement).value;
            const planName = (document.getElementById('edit-plan-name') as HTMLInputElement).value.trim() || "";

            const months: number[] = [];
            for (let i = 0; i < 12; i++) {
                const input = document.getElementById(`edit-month-${i}`) as HTMLInputElement;
                months.push(parseInt(input.value) || 0);
            }

            const planData = {
                id: planId,
                planName,
                months
            };

            try {
                await app.API.updatePlan(planId, planData);
                this.showSuccess('Plan updated successfully!');
                this.hideEditModal();
                await this.loadPlans(); // Refresh the plans list
            } catch (error) {
                this.showError('Failed to update plan. Please try again.');
            }
        } catch (error) {
            console.error('Error updating plan:', error);
            this.showError('An error occurred while updating the plan.');
        }
    }

    private async deletePlan(planId: string) {
        const plan = this.plans.find(p => p.id === planId);
        const planName = plan ? plan.planName : 'this plan';

        if (confirm(`Are you sure you want to delete "${planName}"? This action cannot be undone.`)) {
            try {
                await app.API.deletePlan(planId);
                this.showSuccess('Plan deleted successfully!');
                await this.loadPlans(); // Refresh the plans list
            } catch (error) {
                console.error('Error deleting plan:', error);
                this.showError('An error occurred while deleting the plan.');
            }
        }
    }

    private showError(message: string) {
        // Simple error display - you might want to replace with your preferred notification system
        alert(`Error: ${message}`);
    }

    private showSuccess(message: string) {
        // Simple success display - you might want to replace with your preferred notification system
        alert(message);
    }
}

// Initialize the manager when the page loads
export function initializeSalesReportManager() {
    new SalesReportManager();
}

// Make it available globally for backwards compatibility
(window as any).showGoals = initializeSalesReportManager;